import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, IconButton, Slider } from '@mui/material';
import { useHolidayTheme } from '../../../contexts/holiday-theme-context';
import { Howl } from 'howler';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import './countdown-timer.css';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const HolidayCountdown: React.FC = () => {
  const { isHolidaySeason, holidayType } = useHolidayTheme();
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [targetDate, setTargetDate] = useState<Date | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  
  // Load collapsed state from localStorage, default to true (expanded)
  const [isMusicExpanded, setIsMusicExpandedState] = useState(() => {
    const saved = localStorage.getItem('holiday-countdown-music-expanded');
    return saved !== null ? saved === 'true' : true;
  });

  // Save to localStorage when state changes
  const setIsMusicExpanded = (expanded: boolean) => {
    setIsMusicExpandedState(expanded);
    localStorage.setItem('holiday-countdown-music-expanded', expanded.toString());
  };
  
  const christmasSound = useRef<Howl | null>(null);
  const soundReady = useRef(false);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize Christmas sound
  useEffect(() => {
    if (holidayType === 'christmas') {
      const sound = new Howl({
        src: ['/media/Christmas-Countdown.mp3'],
        volume: 0.5,
        preload: true,
        html5: false,
        onload: () => {
          soundReady.current = true;
          if (sound.duration() > 0) {
            setDuration(sound.duration());
          }
        },
        onplay: () => {
          setIsPlaying(true);
          // Start updating progress
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
          }
          progressIntervalRef.current = setInterval(() => {
            if (christmasSound.current && !isSeeking) {
              const seek = christmasSound.current.seek();
              if (typeof seek === 'number') {
                setCurrentTime(seek);
              }
              const dur = christmasSound.current.duration();
              if (dur && dur > 0 && duration === 0) {
                setDuration(dur);
              }
            }
          }, 100);
        },
        onpause: () => {
          setIsPlaying(false);
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
        },
        onstop: () => {
          setIsPlaying(false);
          setCurrentTime(0);
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
        },
        onend: () => {
          setIsPlaying(false);
          setCurrentTime(0);
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
        },
        onloaderror: (id, error) => {
          console.error('Error loading Christmas sound:', error);
          soundReady.current = false;
        },
      });
      
      christmasSound.current = sound;
      soundReady.current = false;
      
      // Force load immediately
      sound.load();
    } else {
      if (christmasSound.current) {
        christmasSound.current.unload();
        christmasSound.current = null;
        soundReady.current = false;
      }
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (christmasSound.current) {
        christmasSound.current.unload();
        christmasSound.current = null;
        soundReady.current = false;
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
      }
    };
  }, [holidayType]);

  const handleToggleSound = () => {
    if (!christmasSound.current) return;

    if (isPlaying) {
      // If playing, pause it
      christmasSound.current.pause();
    } else {
      // Stop any existing playback
      christmasSound.current.stop();
      
      // Simple approach: if sound is ready, play it. Otherwise, wait for load.
      if (soundReady.current && christmasSound.current.state() === 'loaded') {
        // Sound is ready, play immediately
        christmasSound.current.play();
      } else {
        // Sound not ready yet, set up to play when it loads
        const playWhenReady = () => {
          soundReady.current = true;
          if (christmasSound.current && !isPlaying) {
            christmasSound.current.stop();
            christmasSound.current.play();
          }
        };
        
        // If already loading, wait for load event
        if (christmasSound.current.state() === 'loading') {
          christmasSound.current.once('load', playWhenReady);
        } else if (christmasSound.current.state() === 'unloaded') {
          // Start loading
          christmasSound.current.once('load', playWhenReady);
          christmasSound.current.load();
        } else {
          // State is loaded but onload hasn't fired, try playing anyway
          christmasSound.current.play();
        }
      }
    }
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (_event: Event, newValue: number | number[]) => {
    if (!christmasSound.current || typeof newValue !== 'number') return;
    setIsSeeking(true);
    const seekValue = newValue;
    christmasSound.current.seek(seekValue);
    setCurrentTime(seekValue);
    setIsSeeking(false);
  };

  const handleSeekStart = () => {
    setIsSeeking(true);
  };

  const handleSeekEnd = () => {
    setIsSeeking(false);
  };

  useEffect(() => {
    if (!isHolidaySeason || !holidayType) {
      setTargetDate(null);
      return;
    }

    const now = new Date();
    let target: Date;

    if (holidayType === 'christmas') {
      // Countdown to Christmas (December 25)
      const currentYear = now.getFullYear();
      target = new Date(currentYear, 11, 25, 0, 0, 0); // December 25, midnight
      
      // If Christmas has passed this year, target next year
      if (target < now) {
        target = new Date(currentYear + 1, 11, 25, 0, 0, 0);
      }
    } else {
      // Countdown to New Year (January 1, midnight)
      const currentYear = now.getFullYear();
      target = new Date(currentYear + 1, 0, 1, 0, 0, 0); // January 1, next year
    }

    setTargetDate(target);
  }, [isHolidaySeason, holidayType]);

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  if (!isHolidaySeason || !holidayType || !targetDate) {
    return null;
  }

  const isNewYear = holidayType === 'newyear';
  const title = isNewYear ? 'New Year Countdown' : 'Christmas Countdown';

  // Don't show countdown if the event has passed
  const now = new Date();
  if (targetDate < now) {
    return null;
  }

  // Only show countdown if within 30 days of the event
  const daysUntil = Math.floor((targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysUntil > 30) {
    return null;
  }

  // For Christmas, use festive red and white design
  if (holidayType === 'christmas') {
    return (
      <Box
        sx={{
          padding: '12px 16px',
          backgroundColor: '#FFFFFF',
          border: '2px solid #C8102E',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(200, 16, 46, 0.2)',
          '@media (max-width: 768px)': {
            padding: '10px 12px',
          },
        }}
        className="holiday-countdown"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              sx={{ 
                fontSize: '1.2rem',
                lineHeight: 1,
              }}
            >
              🎄
            </Typography>
            <Typography 
              sx={{ 
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#C8102E',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Christmas
            </Typography>
          </Box>
          <IconButton
            onClick={() => setIsMusicExpanded(!isMusicExpanded)}
            size="small"
            sx={{
              color: '#C8102E',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(200, 16, 46, 0.1)',
              },
            }}
            aria-label={isMusicExpanded ? 'Collapse music controls' : 'Expand music controls'}
          >
            {isMusicExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {timeLeft.days > 0 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                sx={{ 
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#C8102E',
                  lineHeight: 1,
                }}
              >
                {timeLeft.days}
              </Typography>
              <Typography 
                sx={{ 
                  fontSize: '0.65rem',
                  color: '#666',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mt: 0.25,
                }}
              >
                Days
              </Typography>
            </Box>
          )}
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              sx={{ 
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#C8102E',
                lineHeight: 1,
              }}
            >
              {String(timeLeft.hours).padStart(2, '0')}
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '0.65rem',
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mt: 0.25,
              }}
            >
              Hours
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              sx={{ 
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#C8102E',
                lineHeight: 1,
              }}
            >
              {String(timeLeft.minutes).padStart(2, '0')}
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '0.65rem',
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mt: 0.25,
              }}
            >
              Min
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              sx={{ 
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#C8102E',
                lineHeight: 1,
              }}
            >
              {String(timeLeft.seconds).padStart(2, '0')}
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '0.65rem',
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mt: 0.25,
              }}
            >
              Sec
            </Typography>
          </Box>
        </Box>
        {isMusicExpanded && (
          <Box sx={{ mt: 1.5 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: "100%",
                 
              }}
            >
              <Box>
              <IconButton
                onClick={handleToggleSound}
                size="small"
                className={isPlaying ? 'music-button-playing' : 'music-button-paused'}
                sx={{
                  color: '#C8102E',
                  padding: '6px',
                  backgroundColor: isPlaying ? 'rgba(200, 16, 46, 0.1)' : 'rgba(200, 16, 46, 0.05)',
                  border: '2px solid #C8102E',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  boxShadow: isPlaying 
                    ? '0 0 12px rgba(200, 16, 46, 0.4), 0 2px 8px rgba(200, 16, 46, 0.2)' 
                    : '0 2px 8px rgba(200, 16, 46, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(200, 16, 46, 0.15)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 0 16px rgba(200, 16, 46, 0.5), 0 4px 12px rgba(200, 16, 46, 0.3)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
                aria-label={isPlaying ? 'Pause Christmas music' : 'Play Christmas music'}
              >
                {isPlaying ? (
                  <PauseIcon />
                ) : (
                  <PlayArrowIcon />
                )}
              </IconButton>
              </Box>
            
            {/* Progress Bar */}
            {duration > 0 && (
              <Box flex={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: '#666',
                      minWidth: '35px',
                      textAlign: 'right',
                    }}
                  >
                    {formatTime(currentTime)}
                  </Typography>
                  <Slider
                    value={currentTime}
                    max={duration}
                    min={0}
                    step={0.1}
                    onChange={handleSeek}
                    onChangeCommitted={handleSeekEnd}
                    onMouseDown={handleSeekStart}
                    onTouchStart={handleSeekStart}
                    sx={{
                      color: '#C8102E',
                      height: 4,
                      '& .MuiSlider-thumb': {
                        width: 12,
                        height: 12,
                        backgroundColor: '#C8102E',
                        border: '2px solid #FFFFFF',
                        '&:hover': {
                          boxShadow: '0 0 0 8px rgba(200, 16, 46, 0.16)',
                        },
                        '&.Mui-active': {
                          boxShadow: '0 0 0 8px rgba(200, 16, 46, 0.16)',
                        },
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: '#C8102E',
                        border: 'none',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: 'rgba(200, 16, 46, 0.2)',
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: '#666',
                      minWidth: '35px',
                      textAlign: 'left',
                    }}
                  >
                    {formatTime(duration)}
                  </Typography>
                </Box>
              </Box>
              
            )}
              </Box>
          </Box>
        )}
      </Box>
    );
  }

  // For New Year, keep the original design
  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        top: 80,
        right: 16,
        zIndex: 1000,
        padding: 2,
        background: `linear-gradient(135deg, var(--holiday-primary), var(--holiday-accent))`,
        color: 'white',
        borderRadius: 2,
        minWidth: 200,
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        '@media (max-width: 768px)': {
          top: 70,
          right: 8,
          padding: 1.5,
          minWidth: 160,
        },
      }}
      className="holiday-countdown"
    >
      <Typography variant="subtitle2" sx={{ textAlign: 'center', mb: 1, fontWeight: 'bold' }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        {timeLeft.days > 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {timeLeft.days}
            </Typography>
            <Typography variant="caption">Days</Typography>
          </Box>
        )}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {String(timeLeft.hours).padStart(2, '0')}
          </Typography>
          <Typography variant="caption">Hours</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {String(timeLeft.minutes).padStart(2, '0')}
          </Typography>
          <Typography variant="caption">Min</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {String(timeLeft.seconds).padStart(2, '0')}
          </Typography>
          <Typography variant="caption">Sec</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

